/**
 * Módulo Escritório — o painel de quem trabalha no computador.
 *
 * Estoque, aprovações, romaneios e cadastros — navegados pelo MENU LATERAL em
 * cascata, o mesmo componente de todos os módulos de computador. Pedido do
 * Lucas (04/08): a mesma navegação em todo lugar, "para criar uma estabilidade
 * visual". Antes eram abas no topo aqui, chips na Administração e uma barra
 * horizontal nos Pedidos — três jeitos de fazer a mesma coisa.
 *
 * Cadastros é o nó com filhos: a cascata substitui o segundo nível de abas
 * que existia dentro dele.
 */

import { api } from '../api.js';
import { montarPainel } from './painel.js';
import { montarEstoque } from './estoque.js';
import { montarRomaneios } from './romaneios.js';
import { montarAprovacoes } from './aprovacoes.js';
import { montarPedidosFila } from './pedidos-fila.js';
import { montarNovaEntrada } from './nova-entrada.js';
import { montarNovaSaida } from './nova-saida.js';
import { montarValidades } from './validades.js';
import { montarFerramentas } from './ferramentas.js';
import { montarCadastroItens } from './cadastros/itens.js';
import { montarTaxonomia } from './cadastros/taxonomia.js';
import { montarMenuLateral, type ItemMenu } from '../comum/menu-lateral.js';

const TELAS: Record<string, (raiz: HTMLElement) => void> = {
  estoque: montarEstoque,
  aprovacoes: montarAprovacoes,
  pedidos: montarPedidosFila,
  'nova-entrada': montarNovaEntrada,
  'nova-saida': montarNovaSaida,
  romaneios: montarRomaneios,
  validades: montarValidades,
  ferramentas: montarFerramentas,
  'cad-itens': montarCadastroItens,
  'cad-taxonomia': montarTaxonomia,
};

export function montarDesk(raiz: HTMLElement): void {
  raiz.innerHTML = `
    <div class="modulo-com-menu">
      <aside id="menu-desk"></aside>
      <div id="area-desk"></div>
    </div>`;
  const area = raiz.querySelector<HTMLElement>('#area-desk')!;

  const itens = (avisos?: { pedidos?: number; vencidos?: number; recolher?: number }): ItemMenu[] => [
    { chave: 'painel', nome: 'Painel', emoji: '📊' },
    { chave: 'estoque', nome: 'Estoque', emoji: '📦' },
    { chave: 'aprovacoes', nome: 'Aprovações', emoji: '✅' },
    {
      chave: 'pedidos', nome: 'Pedidos', emoji: '🛒',
      ...(avisos?.pedidos ? { badge: avisos.pedidos } : {}),
    },
    { chave: 'nova-entrada', nome: 'Nova entrada', emoji: '📥', separaAntes: true },
    { chave: 'nova-saida', nome: 'Nova saída', emoji: '🚚' },
    { chave: 'romaneios', nome: 'Romaneios', emoji: '📋' },
    {
      chave: 'validades', nome: 'Validades', emoji: '🗓️',
      ...(avisos?.vencidos ? { badge: avisos.vencidos } : {}),
    },
    {
      chave: 'ferramentas', nome: 'Ferramentas', emoji: '🔧',
      ...(avisos?.recolher ? { badge: avisos.recolher } : {}),
    },
    {
      chave: 'cadastros', nome: 'Cadastros', emoji: '🗂️', separaAntes: true,
      // Obras e Pessoas moraram aqui até 02/09 — o Lucas mandou para a
      // Administração: cadastrar gente e obra é decisão de gestão, não de
      // operação do balcão.
      filhos: [
        { chave: 'cad-itens', nome: 'Itens' },
        { chave: 'cad-taxonomia', nome: 'Categorias' },
      ],
    },
  ];

  const abrir = (chave: string): void => {
    // O nó "Cadastros" em si abre a primeira tela dele: clicar no pai não
    // pode ser um beco.
    const efetiva = chave === 'cadastros' ? 'cad-itens' : chave;
    if (efetiva === 'painel') {
      // O painel navega: clicar num número (aprovações pendentes, vencidos…)
      // leva direto à aba que resolve.
      montarPainel(area, abrir);
    } else {
      TELAS[efetiva]?.(area);
    }
    menu.marcar(efetiva);
  };

  const menu = montarMenuLateral(
    raiz.querySelector<HTMLElement>('#menu-desk')!,
    itens(),
    abrir,
  );

  // Abre no Painel — como o desk antigo abria no dashboard.
  abrir('painel');

  // Os avisos do menu (pedidos na fila, lotes vencidos), sem atrasar a
  // abertura da tela — chegam por trás e o menu se refaz.
  void Promise.allSettled([
    api.get<{ pedidos: { status: string }[] }>('/api/pedidos/fila'),
    api.get<{ vencidos: number }>('/api/validades/resumo'),
    api.get<{ unidades: { vencido: boolean }[] }>('/api/ferramentas?situacao=emprestada'),
  ]).then(([fila, val, fer]) => {
    const avisos = {
      pedidos:
        fila.status === 'fulfilled'
          ? fila.value.pedidos.filter((p) => p.status !== 'separado_parcial').length
          : 0,
      vencidos: val.status === 'fulfilled' ? val.value.vencidos : 0,
      // Ferramentas com prazo vencido: o aviso de RECOLHER que o Lucas pediu.
      recolher:
        fer.status === 'fulfilled' ? fer.value.unidades.filter((u) => u.vencido).length : 0,
    };
    if (avisos.pedidos || avisos.vencidos || avisos.recolher) menu.reabastecer(itens(avisos));
  });
}
